import React, {
	useCallback,
	useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import ChatWindow from './ChatWindow';
import usePreventScroll from '../../../../Hooks/usePreventScroll';
import { isServer } from '../../../../../lib/helpers';

export const ChatContextUI = React.createContext({
	ChatWindow: React.createElement('div'),
});
export const ChatContextUnreadMessages = React.createContext({
	unReadMessagesCount: 0,
});
export const ChatContextControls = React.createContext({
	setVisible: () => ({}),
	resetChat: () => ({}),
	joinChat: () => ({}),
});
const ChatManager = ({ children }) => {
	const [messages, setMessages] = useState([]);
	const [unReadMessages, setUnReadMessage] = useState(0);
	const [visible, setVisible] = useState(!isServer && window.innerWidth > 620);
	const socketRef = useRef({});
	const inputRef = useRef({});

	useEffect(() => {
		if (visible && inputRef?.current?.focus) {
			inputRef.current.focus();
		}
	}, [visible]);

	/**
	 * prevent scroll on mobile when chat is opened
	 */
	usePreventScroll(visible);

	/**
	 * @param socket {SocketIOClient}
	 */
	const joinChat = useCallback((socket) => {
		socketRef.current = socket;
		// @NOTE clear listener in case we are already subscribed
		socketRef.current.off('chatMessage');
		socketRef.current.on('chatMessage', (data) => {
			setUnReadMessage(prevState => prevState + 1);
			setMessages(prevState => [...prevState, {
				myMessage: false,
				message: data.message,
			}]);
		});
	}, []);

	/**
	 * @param message {string}
	 * @returns Promise
	 */
	const sendMessage = useCallback((message) => {
		// TODO Add message delivery validation!
		if (!socketRef.current || typeof socketRef.current.emit !== 'function') {
			return Promise.reject(new Error('No socket connection'));
		}
		socketRef.current.emit('chatMessage', { message });
		setMessages(prevState => [...prevState, {
			myMessage: true,
			message,
		}]);
		return Promise.resolve();
	}, []);

	const Chat = useMemo(() => (
		<ChatWindow
			messages={messages}
			addMessage={sendMessage}
			resetMessagesUnReadCount={() => setUnReadMessage(0)}
			visible={visible}
			setVisible={setVisible}
			ref={inputRef}
		/>
	), [messages, sendMessage, visible]);

	const ChatContextControlsMemoized = useMemo(() => ({
		resetChat: () => {
			setMessages([]);
			setUnReadMessage(0);
		},
		setVisible,
		joinChat,
	}), [joinChat]);

	return (
		<ChatContextUI.Provider value={Chat}>
			<ChatContextUnreadMessages.Provider value={unReadMessages}>
				<ChatContextControls.Provider value={ChatContextControlsMemoized}>
					{children}
				</ChatContextControls.Provider>
			</ChatContextUnreadMessages.Provider>
		</ChatContextUI.Provider>
	);
};

ChatManager.defaultProps = {
	children: null,
};

ChatManager.propTypes = {
	children: PropTypes.node,
};

export default ChatManager;
